import axios from 'axios';
import { shouldRateLimit, retryWithBackoff } from './notificationUtils';
import config from '../config';
import storage from '../utils/storage';

const BASE_URL = config.backend.host;
const API_BASE_URL = `${BASE_URL}/api`;

// Log API configuration on module load
console.log('🔧 API Configuration:', {
    BASE_URL,
    API_BASE_URL,
    configBackend: config.backend
});

// Configure axios defaults
axios.defaults.withCredentials = true; // Important for CORS with credentials

// Set timeout for requests (30 seconds)
axios.defaults.timeout = 30000;

// WebView-specific configuration for Android
if (typeof window !== 'undefined') {
  // Detect if running in WebView
  const isWebView = /wv/i.test(navigator.userAgent) || 
                    window.location.protocol === 'capacitor:' ||
                    window.location.protocol === 'capacitor-http:' ||
                    window.location.protocol === 'capacitor-https:';
  
  if (isWebView) {
    console.log('🌐 WebView detected - applying WebView-specific config');
    // Ensure proper headers for WebView
    axios.defaults.headers.common['Accept'] = 'application/json';
    axios.defaults.headers.common['Content-Type'] = 'application/json';
  }
}

// CSRF token cache
let csrfToken = null;
let csrfTokenExpiry = null;
const CSRF_TOKEN_VALIDITY = 30 * 60 * 1000; // 30 minutes
let isFetchingCSRF = false;
let lastCSRFFetchTime = 0;
const MIN_CSRF_FETCH_INTERVAL = 1000; // 1 second minimum between CSRF fetches
let csrfRequestCount = 0; // Debug counter

const BookMyOpAPI = {
    // Get CSRF Token with caching and rate limiting
    getCSRFToken: async (forceRefresh = false) => {
        csrfRequestCount++;
        console.log(`CSRF token request #${csrfRequestCount} - forceRefresh: ${forceRefresh}`);
        console.log(`CSRF token request - API_BASE_URL: ${API_BASE_URL}`);
        
        // Return cached token if it's still valid and not forcing refresh
        if (!forceRefresh && csrfToken && csrfTokenExpiry && Date.now() < csrfTokenExpiry) {
            console.log(`CSRF token request #${csrfRequestCount} - using cached token`);
            return { csrfToken };
        }
        
        // Prevent multiple simultaneous CSRF fetches
        if (isFetchingCSRF) {
            console.log(`CSRF token request #${csrfRequestCount} - skipped: already fetching`);
            return { csrfToken };
        }
        
        // Prevent too frequent CSRF fetches
        const now = Date.now();
        if (!forceRefresh && now - lastCSRFFetchTime < MIN_CSRF_FETCH_INTERVAL) {
            console.log(`CSRF token request #${csrfRequestCount} - skipped: too frequent`);
            return { csrfToken };
        }
        
        isFetchingCSRF = true;
        lastCSRFFetchTime = now;
        
        try {
            const csrfUrl = `${API_BASE_URL}/bookmyop/login/`;
            console.log(`CSRF token request #${csrfRequestCount} - fetching from: ${csrfUrl}`);
            const response = await axios.get(csrfUrl);
            console.log(`CSRF token response status: ${response.status}`);
            console.log(`CSRF token response data:`, response.data);
            
            if (response.data.csrfToken) {
                csrfToken = response.data.csrfToken;
                csrfTokenExpiry = Date.now() + CSRF_TOKEN_VALIDITY;
                axios.defaults.headers.common['X-CSRFToken'] = csrfToken;
                console.log(`CSRF token request #${csrfRequestCount} - fetched successfully`);
            }
            return response.data;
        } catch (error) {
            console.error(`CSRF token request #${csrfRequestCount} - failed:`, error);
            console.error('CSRF error details:', {
                message: error.message,
                code: error.code,
                response: error.response?.data,
                status: error.response?.status,
                statusText: error.response?.statusText,
                config: {
                    url: error.config?.url,
                    method: error.config?.method,
                    baseURL: error.config?.baseURL
                }
            });
            // Clear cached token on error
            csrfToken = null;
            csrfTokenExpiry = null;
            throw error;
        } finally {
            isFetchingCSRF = false;
        }
    },

    // Authentication
    register: async (userData) => {
        try {
            await BookMyOpAPI.getCSRFToken(); // Get CSRF token before registration
            const response = await axios.post(`${API_BASE_URL}/bookmyop/patients/`, userData);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Registration failed' };
        }
    },

    // Send OTP
    sendOTP: async (phoneNumber) => {
        try {
            // Try to get CSRF token, but don't fail if it fails (some endpoints might work without it)
        try {
            await BookMyOpAPI.getCSRFToken();
            } catch (csrfError) {
                console.warn('CSRF token fetch failed, continuing with OTP send:', csrfError);
                // Continue anyway - backend might handle OTP without CSRF
            }
            
            console.log('Sending OTP to:', `${API_BASE_URL}/bookmyop/login/send-otp/`);
            console.log('Phone number:', phoneNumber);
            
            const response = await axios.post(`${API_BASE_URL}/bookmyop/login/send-otp/`, {
                phone_number: phoneNumber
            });
            
            console.log('OTP send response:', response.data);
            return response.data;
        } catch (error) {
            console.error('OTP send error:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                statusText: error.response?.statusText,
                config: {
                    url: error.config?.url,
                    method: error.config?.method,
                    baseURL: error.config?.baseURL
                }
            });
            
            // Provide more detailed error messages
            let errorMessage = 'Failed to send OTP';
            
            if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error') || !error.response) {
                errorMessage = 'Network error. Please check your internet connection and try again.';
            } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                errorMessage = 'Request timeout. Please check your connection and try again.';
            } else if (error.response?.status === 400) {
                errorMessage = error.response?.data?.message || 'Invalid phone number. Please check and try again.';
            } else if (error.response?.status === 429) {
                errorMessage = 'Too many requests. Please wait a moment and try again.';
            } else if (error.response?.status >= 500) {
                errorMessage = 'Server error. Please try again later.';
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            throw { 
                message: errorMessage,
                originalError: error
            };
        }
    },

    // Verify OTP
    verifyOTP: async (phoneNumber, otp) => {
        try {
            await BookMyOpAPI.getCSRFToken();
            const response = await axios.post(`${API_BASE_URL}/bookmyop/login/verify-otp/`, {
                phone_number: phoneNumber,
                otp: otp
            });
            
            // Store tokens if verification successful
            if (response.data.tokens) {
                await storage.setItem('bookmyop_access_token', response.data.tokens.access);
                await storage.setItem('bookmyop_refresh_token', response.data.tokens.refresh);
                axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.tokens.access}`;
            }
            
            return response.data;
        } catch (error) {
            throw error.response?.data || { 
                message: error.response?.data?.message || 'OTP verification failed'
            };
        }
    },

    // Login (now supports both email and OTP login)
    login: async (credentials) => {
        try {
            await BookMyOpAPI.getCSRFToken(); // Get CSRF token before login
            const response = await axios.post(`${API_BASE_URL}/bookmyop/login/`, credentials);
            
            // Store tokens if login successful
            if (response.data.tokens) {
                await storage.setItem('bookmyop_access_token', response.data.tokens.access);
                await storage.setItem('bookmyop_refresh_token', response.data.tokens.refresh);
                // Set default authorization header for future requests
                axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.tokens.access}`;
            }
            
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Login failed' };
        }
    },

    logout: async () => {
        await storage.removeItem('bookmyop_access_token');
        await storage.removeItem('bookmyop_refresh_token');
        delete axios.defaults.headers.common['Authorization'];
        delete axios.defaults.headers.common['X-CSRFToken'];
        // Clear CSRF token cache on logout
        csrfToken = null;
        csrfTokenExpiry = null;
        isFetchingCSRF = false;
        lastCSRFFetchTime = 0;
        csrfRequestCount = 0; // Reset debug counter
        // Clear session location data
        sessionStorage.removeItem('session_location_set');
        sessionStorage.removeItem('guest_location_data');
    },

    // Specialties
    getSpecialties: async () => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.get(`${API_BASE_URL}/bookmyop/specialties/`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch specialties' };
        }
    },

    // Service Providers
    getServiceProviders: async (filters = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            const queryParams = new URLSearchParams();
            
            // Add filters to query params
            if (filters.search) queryParams.append('search', filters.search);
            if (filters.providerType) queryParams.append('provider_type', filters.providerType);
            if (filters.specialty) queryParams.append('specialties', filters.specialty);
            
            // Add date filters
            if (filters.availableDate) queryParams.append('available_date', filters.availableDate);
            if (filters.availableDateStart) queryParams.append('available_date_start', filters.availableDateStart);
            if (filters.availableDateEnd) queryParams.append('available_date_end', filters.availableDateEnd);
            
            // Add pagination parameters
            if (filters.page) queryParams.append('page', filters.page);
            if (filters.pageSize) queryParams.append('page_size', filters.pageSize);
            
            // Add sorting parameters
            if (filters.ordering) queryParams.append('ordering', filters.ordering);
            
            const response = await axios.get(`${API_BASE_URL}/bookmyop/providers/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch service providers' };
        }
    },

    // Doctors
    getDoctors: async (filters = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            const queryParams = new URLSearchParams();
            
            // Add filters to query params
            if (filters.search) queryParams.append('search', filters.search);
            if (filters.specialty) queryParams.append('specialty', filters.specialty);
            if (filters.serviceProvider) queryParams.append('service_provider', filters.serviceProvider);
            if (filters.qualifications) queryParams.append('qualifications', filters.qualifications);
            
            // Add date filters
            if (filters.availableDate) queryParams.append('available_date', filters.availableDate);
            if (filters.availableDateStart) queryParams.append('available_date_start', filters.availableDateStart);
            if (filters.availableDateEnd) queryParams.append('available_date_end', filters.availableDateEnd);
            
            // Add pagination parameters
            if (filters.page) queryParams.append('page', filters.page);
            if (filters.pageSize) queryParams.append('page_size', filters.pageSize);
            
            // Add sorting parameters
            if (filters.ordering) queryParams.append('ordering', filters.ordering);
            
            const response = await axios.get(`${API_BASE_URL}/bookmyop/doctors/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch doctors' };
        }
    },

    // Doctors by Fee (Lowest/Highest fee first)
    getDoctorsByFee: async (filters = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            const queryParams = new URLSearchParams();
            
            // Add pagination parameters
            if (filters.page) queryParams.append('page', filters.page);
            if (filters.pageSize) queryParams.append('page_size', filters.pageSize);
            
            // Add sorting order (asc for lowest fee first, desc for highest fee first)
            if (filters.sortOrder) queryParams.append('sort_order', filters.sortOrder);
            
            // Add fee range filters
            if (filters.minFee !== undefined) queryParams.append('min_fee', filters.minFee);
            if (filters.maxFee !== undefined) queryParams.append('max_fee', filters.maxFee);
            
            // Add specialty filter
            if (filters.specialtyId) queryParams.append('specialty_id', filters.specialtyId);
            
            const response = await axios.get(`${API_BASE_URL}/bookmyop/doctors/by-fee/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch doctors by fee' };
        }
    },

    // Senior Doctors (Sorted by Experience)
    getSeniorDoctors: async (filters = {}) => {
        try {
            const queryParams = new URLSearchParams();
            
            // Add pagination parameters
            if (filters.page) queryParams.append('page', filters.page);
            if (filters.pageSize) queryParams.append('page_size', filters.pageSize);
            
            // Add specialty filter
            if (filters.specialtyId) queryParams.append('specialty_id', filters.specialtyId);
            
            // Add experience range filters
            if (filters.minExperience !== undefined) queryParams.append('min_experience', filters.minExperience);
            if (filters.maxExperience !== undefined) queryParams.append('max_experience', filters.maxExperience);
            
            const response = await axios.get(`${API_BASE_URL}/bookmyop/senior/doctors/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch senior doctors' };
        }
    },

    // High-Rated Doctors (No authentication required)
    getHighRatedDoctors: async (filters = {}) => {
        try {
            const queryParams = new URLSearchParams();
            
            // Add filters to query params
            if (filters.limit) queryParams.append('limit', filters.limit);
           
            if (filters.specialtyId) queryParams.append('specialty_id', filters.specialtyId);
            if (filters.city) queryParams.append('city', filters.city);
            if (filters.state) queryParams.append('state', filters.state);
            
            const response = await axios.get(`${API_BASE_URL}/bookmyop/high-rated/doctors/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch high-rated doctors' };
        }
    },

    // Nearest Doctors (Authentication required)
    getNearestDoctors: async (filters = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            const queryParams = new URLSearchParams();
            
            // Add filters to query params
            if (filters.limit) queryParams.append('limit', filters.limit);
            if (filters.maxDistance) queryParams.append('max_distance', filters.maxDistance);
            if (filters.specialtyId) queryParams.append('specialty_id', filters.specialtyId);
            
            const response = await axios.get(`${API_BASE_URL}/bookmyop/nearest/doctors/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch nearest doctors' };
        }
    },

    // Top-Rated Providers (No authentication required)
    getTopRatedProviders: async (filters = {}) => {
        try {
            const queryParams = new URLSearchParams();
            
            // Add filters to query params
            if (filters.limit) queryParams.append('limit', filters.limit);
           
            if (filters.providerType) queryParams.append('provider_type', filters.providerType);
            if (filters.city) queryParams.append('city', filters.city);
            if (filters.state) queryParams.append('state', filters.state);
            
            const response = await axios.get(`${API_BASE_URL}/bookmyop/top-rated/providers/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch top-rated providers' };
        }
    },

    // Nearest Providers (Authentication required)
    getNearestProviders: async (filters = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            const queryParams = new URLSearchParams();
            
            // Add filters to query params
            if (filters.limit) queryParams.append('limit', filters.limit);
            if (filters.maxDistance) queryParams.append('max_distance', filters.maxDistance);
            if (filters.providerType) queryParams.append('provider_type', filters.providerType);
            
            const response = await axios.get(`${API_BASE_URL}/bookmyop/nearest/providers/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch nearest providers' };
        }
    },

    // Create Appointment
    createAppointment: async (appointmentData) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            if (appointmentData instanceof FormData) {
                const response = await axios.post(
                    `${API_BASE_URL}/bookmyop/appointments/create/`, 
                    appointmentData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
                return response.data;
            } else {
                // For backward compatibility
                const formData = new FormData();
                formData.append('service_provider', appointmentData.service_provider);
                formData.append('specialty', appointmentData.specialty);
                formData.append('doctor', appointmentData.doctor);
                formData.append('appointment_date', appointmentData.appointment_date);
                formData.append('appointment_time', appointmentData.appointment_time);
                
                if (appointmentData.documents && appointmentData.documents.length > 0) {
                    appointmentData.documents.forEach((doc, index) => {
                        formData.append(`documents[${index}][file]`, doc);
                        formData.append(`documents[${index}][document_type]`, doc.document_type || 'Other');
                    });
                }
                
                const response = await axios.post(
                    `${API_BASE_URL}/bookmyop/appointments/create/`, 
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
                return response.data;
            }
        } catch (error) {
            throw error.response?.data || { message: 'Failed to create appointment' };
        }
    },

    // Get User Appointments
    getAppointments: async (filters = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.get(`${API_BASE_URL}/bookmyop/appointments/`, { params: filters });
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch appointments' };
        }
    },

    // Favorites API
    getFavorites: async (filters = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.get(`${API_BASE_URL}/bookmyop/favorites/`, { params: filters });
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch favorites' };
        }
    },

    addToFavorites: async (favoriteData) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.post(`${API_BASE_URL}/bookmyop/favorites/`, favoriteData);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to add to favorites' };
        }
    },

    removeFromFavorites: async (favoriteId) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.delete(`${API_BASE_URL}/bookmyop/favorites/${favoriteId}/`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to remove from favorites' };
        }
    },

    toggleFavorite: async (favoriteData) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.post(`${API_BASE_URL}/bookmyop/favorites/toggle/`, favoriteData);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to toggle favorite' };
        }
    },

    checkFavoriteStatus: async (params) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.get(`${API_BASE_URL}/bookmyop/favorites/check/`, { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to check favorite status' };
        }
    },

    getFavoriteDoctors: async () => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.get(`${API_BASE_URL}/bookmyop/favorites/doctors/`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch favorite doctors' };
        }
    },

    getFavoriteProviders: async () => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.get(`${API_BASE_URL}/bookmyop/favorites/providers/`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch favorite providers' };
        }
    },

    // Cancel Appointment
    cancelAppointment: async (appointmentId, reason) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.post(`${API_BASE_URL}/bookmyop/appointments/${appointmentId}/cancel/`, {
                cancellation_reason: reason
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to cancel appointment' };
        }
    },

    // Communications
    getCommunications: async (filters = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            // Build query parameters for enhanced filtering and pagination
            const queryParams = new URLSearchParams();
            
            // Pagination parameters
            if (filters.page) queryParams.append('page', filters.page);
            if (filters.pageSize) queryParams.append('page_size', filters.pageSize);
            
            // Filter parameters
            if (filters.status) queryParams.append('status', filters.status);
            if (filters.receiverType) queryParams.append('receiver_type', filters.receiverType);
            if (filters.search) queryParams.append('search', filters.search);
            if (filters.ordering) queryParams.append('ordering', filters.ordering);
            
            // Backward compatibility - pass through any other filters
            Object.keys(filters).forEach(key => {
                if (!['page', 'pageSize', 'status', 'receiverType', 'search', 'ordering'].includes(key)) {
                    queryParams.append(key, filters[key]);
                }
            });
            
            const url = `${API_BASE_URL}/bookmyop/communications/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch communications' };
        }
    },

    // Get service providers for communication
    getServiceProviders: async () => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.get(`${API_BASE_URL}/bookmyop/communications/service_providers/`);
            return response.data.data || response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch service providers' };
        }
    },

    // Get replies for a specific communication
    getReplies: async (id) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.get(`${API_BASE_URL}/bookmyop/communications/${id}/replies/`);
            return response.data.data || response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch replies' };
        }
    },

    getCommunicationById: async (id) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.get(`${API_BASE_URL}/bookmyop/communications/${id}/`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch communication details' };
        }
    },

    createCommunication: async (data) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const formData = new FormData();
            
            // Append text fields
            formData.append('subject', data.subject);
            formData.append('message', data.message);
            
            // Append new receiver type fields
            if (data.receiverType) {
                formData.append('receiver_type', data.receiverType);
            }
            
            if (data.receiverType === 'service_provider' && data.serviceProviderId) {
                formData.append('service_provider_id', data.serviceProviderId);
            }
            
            // Append attachment if exists
            if (data.attachment) {
                formData.append('attachment', data.attachment);
            }
            
            const response = await axios.post(`${API_BASE_URL}/bookmyop/communications/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to create communication' };
        }
    },

    replyCommunication: async (id, data) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const formData = new FormData();
            
            // Append message
            formData.append('message', data.message);
            
            // Append attachment if exists
            if (data.attachment) {
                formData.append('attachment', data.attachment);
            }
            
            const response = await axios.post(
                `${API_BASE_URL}/bookmyop/communications/${id}/reply/`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to reply to communication' };
        }
    },

    closeCommunication: async (id) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.post(`${API_BASE_URL}/bookmyop/communications/${id}/close/`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to close communication' };
        }
    },

    reopenCommunication: async (id) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.post(`${API_BASE_URL}/bookmyop/communications/${id}/reopen/`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to reopen communication' };
        }
    },

    // Profile Management
    getProfile: async () => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.get(`${API_BASE_URL}/bookmyop/profile/`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch profile' };
        }
    },

    updateProfile: async (data, headers = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.patch(`${API_BASE_URL}/bookmyop/profile/`, data, { headers });
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to update profile' };
        }
    },

    // Setup interceptor for token handling
    setupAxiosInterceptors: () => {
        // Request interceptor
        axios.interceptors.request.use(
            config => {
                // Don't add auth headers to session location endpoint (for guest users)
                const isSessionLocation = config.url?.includes('/session-location/');
                
                // Only add Authorization header if:
                // 1. Not a session location request
                // 2. Token exists
                // 3. Authorization header not explicitly set to undefined
                // Use sync read for request interceptor (needs to be synchronous)
                const token = storage.getItemSync('bookmyop_access_token');
                if (token && !isSessionLocation && config.headers.Authorization !== undefined) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                
                // Add location headers from stored user data if available
                // Backend requires location for most API calls
                if (!isSessionLocation) {
                    let latitude = null;
                    let longitude = null;
                    
                    try {
                        // Try multiple methods to get location data
                        // Method 1: Use storage utility (sync read)
                        const userDataStr = storage.getItemSync('bookmyop_user_data');
                        if (userDataStr) {
                            try {
                                const userData = JSON.parse(userDataStr);
                                // Try multiple location fields (backend might use different field names)
                                latitude = userData.latitude || userData.current_latitude || userData.lat;
                                longitude = userData.longitude || userData.current_longitude || userData.lng;
                            } catch (parseErr) {
                                console.warn('⚠️ Failed to parse user data for location:', parseErr);
                            }
                        }
                        
                        // Method 2: Fallback to direct localStorage read (for web)
                        if ((!latitude || !longitude) && typeof localStorage !== 'undefined') {
                            try {
                                const directUserDataStr = localStorage.getItem('bookmyop_user_data');
                                if (directUserDataStr) {
                                    const userData = JSON.parse(directUserDataStr);
                                    latitude = userData.latitude || userData.current_latitude || userData.lat;
                                    longitude = userData.longitude || userData.current_longitude || userData.lng;
                                }
                            } catch (directErr) {
                                console.warn('⚠️ Direct localStorage read failed for location:', directErr);
                            }
                        }
                        
                        // Handle string values that might be stored as strings
                        if (latitude && typeof latitude === 'string') {
                            latitude = parseFloat(latitude);
                        }
                        if (longitude && typeof longitude === 'string') {
                            longitude = parseFloat(longitude);
                        }
                        
                        // Validate coordinates are numbers
                        if (isNaN(latitude) || isNaN(longitude)) {
                            latitude = null;
                            longitude = null;
                        }
                        
                        // Add headers if we found valid location data
                        // Handle both string and number formats
                        const latValue = (latitude && !isNaN(latitude)) ? String(latitude).trim() : null;
                        const lngValue = (longitude && !isNaN(longitude)) ? String(longitude).trim() : null;
                        
                        if (latValue && lngValue && latValue !== 'null' && lngValue !== 'null') {
                            config.headers['X-Latitude'] = latValue;
                            config.headers['X-Longitude'] = lngValue;
                            // Log first few requests to verify it's working
                            if (!config._locationHeaderLogged) {
                                console.log('📍 Added location headers to request:', { 
                                    latitude: latValue, 
                                    longitude: lngValue,
                                    url: config.url 
                                });
                                config._locationHeaderLogged = true;
                            }
                        } else {
                            // Log when location is missing (but not on every request)
                            if (Math.random() < 0.1) { // Log 10% of requests to help debug
                                console.warn('⚠️ Location headers not added:', {
                                    hasLatitude: !!latitude,
                                    hasLongitude: !!longitude,
                                    latitudeValue: latitude,
                                    longitudeValue: longitude,
                                    url: config.url
                                });
                            }
                        }
                    } catch (err) {
                        // Only log errors, not on every request
                        if (Math.random() < 0.1) { // Log 10% of errors
                            console.error('Error reading user data for location headers:', err);
                        }
                    }
                }
                
                // Clean up undefined headers
                if (config.headers.Authorization === undefined) {
                    delete config.headers.Authorization;
                }
                
                return config;
            },
            error => {
                return Promise.reject(error);
            }
        );

        // Response interceptor
        axios.interceptors.response.use(
            response => response,
            async error => {
                const originalRequest = error.config;
                
                // If error is 401 and we haven't tried to refresh token yet
                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;
                    
                    try {
                        // Try sync first, then async for mobile
                        let refreshToken = storage.getItemSync('bookmyop_refresh_token');
                        if (!refreshToken) {
                            console.log('🔍 Refresh token not found in sync storage, trying async...');
                            refreshToken = await storage.getItem('bookmyop_refresh_token');
                        }
                        if (!refreshToken) {
                            console.error('❌ No refresh token available after checking both sync and async storage');
                            console.log('⚠️ NOT clearing auth data - tokens may still be valid, just not accessible yet');
                            // DON'T clear tokens here - they might still be valid
                            // Only clear if we get a definitive error from the refresh endpoint
                            // Create a custom error to indicate auth failure
                            const authError = new Error('No refresh token available - please login again');
                            authError.isAuthError = true;
                            throw authError;
                        }
                        console.log('✅ Refresh token found, attempting token refresh...');
                        
                        // Only get CSRF token if we don't have a valid one
                        if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                            try {
                                await BookMyOpAPI.getCSRFToken();
                            } catch (csrfError) {
                                console.warn('⚠️ CSRF token fetch failed during refresh, continuing anyway:', csrfError);
                                // Continue without CSRF - some endpoints might work without it
                            }
                        }
                        
                        // Call refresh token endpoint
                        // Try multiple possible endpoint paths (bookmyop endpoints are under /bookmyop/)
                        const refreshEndpoints = [
                            `${API_BASE_URL}/bookmyop/token/refresh/`, // Most likely - matches other bookmyop endpoints
                            `${API_BASE_URL}/token/refresh/`, // Standard Django REST framework path
                            `${BASE_URL}/api/token/refresh/` // Fallback
                        ];
                        
                        let response = null;
                        let refreshError = null;
                        
                        for (const endpoint of refreshEndpoints) {
                            try {
                                console.log('🔄 Attempting token refresh at:', endpoint);
                                // Don't send Authorization header for token refresh (it uses refresh token in body)
                                response = await axios.post(
                                    endpoint, 
                                    {
                                        refresh: refreshToken
                                    },
                                    {
                                        headers: {
                                            'Authorization': undefined, // Don't send auth header for refresh
                                            'Content-Type': 'application/json'
                                        },
                                        withCredentials: true // Send session cookie
                                    }
                                );
                                console.log('✅ Token refresh successful at:', endpoint);
                                break; // Success, exit loop
                            } catch (err) {
                                refreshError = err;
                                console.warn(`⚠️ Token refresh failed at ${endpoint}:`, err.response?.status || err.message);
                                // Continue to next endpoint
                            }
                        }
                        
                        if (!response) {
                            // All endpoints failed, throw the last error
                            throw refreshError || new Error('Token refresh failed on all endpoints');
                        }
                        
                        const { access } = response.data;
                        
                        // Update stored token and headers
                        await storage.setItem('bookmyop_access_token', access);
                        axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;
                        
                        // Retry the original request
                        originalRequest.headers['Authorization'] = `Bearer ${access}`;
                        return axios(originalRequest);
                    } catch (refreshError) {
                        console.error('❌ Token refresh failed on all endpoints:', refreshError);
                        // Only clear tokens if we get a definitive auth error (401, 403)
                        // Don't clear on 404 - that just means the endpoint path might be wrong
                        // Don't clear on network errors - tokens might still be valid
                        const isDefinitiveAuthError = refreshError.response?.status === 401 || 
                                                      refreshError.response?.status === 403 ||
                                                      refreshError.isAuthError;
                        
                        if (isDefinitiveAuthError) {
                            console.log('⚠️ Definitive auth error from refresh endpoint (status:', refreshError.response?.status, '), clearing tokens');
                            await storage.removeItem('bookmyop_access_token');
                            await storage.removeItem('bookmyop_refresh_token');
                            await storage.removeItem('bookmyop_user_data');
                            
                            // Don't redirect immediately - let the auth context handle it
                            // The auth context will keep user logged in with stored data if available
                            // Only redirect if user explicitly tries to access a protected route
                            console.log('⚠️ Tokens cleared due to auth failure. User will be logged out on next protected route access.');
                        } else {
                            // Token refresh failed but not a definitive auth error (e.g., 404 - endpoint doesn't exist)
                            // This could mean:
                            // 1. Token refresh endpoint doesn't exist (404) - tokens might still be valid
                            // 2. Network error - tokens might still be valid
                            // 3. Other transient error
                            // Keep tokens and try the original request - maybe tokens are still valid
                            console.log('⚠️ Token refresh failed but keeping tokens (status:', refreshError.response?.status, ')');
                            console.log('💡 Attempting original request with existing token - it might still be valid');
                            
                            // Try the original request with the existing token
                            // If it fails with 401 again, we'll handle it
                            const token = storage.getItemSync('bookmyop_access_token');
                            if (token) {
                                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                                // Reset retry flag so we can try again if this also fails
                                // But limit to prevent infinite loops
                                if (!originalRequest._retryCount) {
                                    originalRequest._retryCount = 0;
                                }
                                originalRequest._retryCount++;
                                if (originalRequest._retryCount < 2) {
                                    originalRequest._retry = false; // Allow one more retry
                                }
                            }
                            
                            // Retry the original request
                            return axios(originalRequest);
                        }
                        delete axios.defaults.headers.common['Authorization'];
                        delete axios.defaults.headers.common['X-CSRFToken'];
                        // Clear CSRF token cache
                        csrfToken = null;
                        csrfTokenExpiry = null;
                        isFetchingCSRF = false;
                        lastCSRFFetchTime = 0;
                        csrfRequestCount = 0; // Reset debug counter
                        
                        // Create a custom error to indicate auth failure
                        const authError = new Error('Authentication failed - please login again');
                        authError.isAuthError = true;
                        return Promise.reject(authError);
                    }
                }
                return Promise.reject(error);
            }
        );
    },

    // Upload documents for an existing appointment
    uploadAppointmentDocuments: async (formData) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            const response = await axios.post(
                `${API_BASE_URL}/bookmyop/patient-documents/`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Upload documents error:', error.response || error);
            throw error.response?.data || { message: 'Failed to upload documents' };
        }
    },

    // Remove a document from an appointment
    removeAppointmentDocument: async (documentId) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            const response = await axios.delete(
                `${API_BASE_URL}/bookmyop/patient-documents/${documentId}/`,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Remove document error:', error.response || error);
            throw error.response?.data || { message: 'Failed to remove document' };
        }
    },

    // Patient Documents CRUD
    // List documents with filtering, search, and pagination
    listPatientDocuments: async (filters = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.get(`${API_BASE_URL}/bookmyop/patient-documents/`, { params: filters });
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch patient documents' };
        }
    },

    // Retrieve a single document by ID
    getPatientDocument: async (documentId) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.get(`${API_BASE_URL}/bookmyop/patient-documents/${documentId}/`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch document details' };
        }
    },

    // Update a document (PUT or PATCH)
    updatePatientDocument: async (documentId, data, method = 'patch') => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const url = `${API_BASE_URL}/bookmyop/patient-documents/${documentId}/`;
            let response;
            if (method === 'put') {
                response = await axios.put(url, data);
            } else {
                response = await axios.patch(url, data);
            }
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to update document' };
        }
    },

    // Notifications
    getNotifications: async (filters = {}) => {
        // Rate limit notification calls
        if (shouldRateLimit('getNotifications', 2000)) {
            console.warn('Rate limited: getNotifications called too frequently');
            return { results: [], count: 0 };
        }

        return retryWithBackoff(async () => {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            // Build query parameters for filtering and pagination
            const queryParams = new URLSearchParams();
            
            // Pagination parameters
            if (filters.page) queryParams.append('page', filters.page);
            if (filters.pageSize) queryParams.append('page_size', filters.pageSize);
            
            // Filter parameters
            if (filters.isRead !== undefined) queryParams.append('is_read', filters.isRead);
            if (filters.notificationType) queryParams.append('notification_type', filters.notificationType);
            if (filters.ordering) queryParams.append('ordering', filters.ordering);
            
            const url = `${API_BASE_URL}/bookmyop/notifications/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
            const response = await axios.get(url);
            return response.data;
        }, 2, 1000);
    },

    markNotificationAsRead: async (notificationId) => {
        // Rate limit mark as read calls
        if (shouldRateLimit(`markAsRead_${notificationId}`, 1000)) {
            console.warn('Rate limited: markAsRead called too frequently for notification', notificationId);
            return { success: true };
        }

        return retryWithBackoff(async () => {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.patch(`${API_BASE_URL}/bookmyop/notifications/${notificationId}/`, {
                is_read: true
            });
            return response.data;
        }, 2, 500);
    },

    markAllNotificationsAsRead: async () => {
        // Rate limit mark all as read calls
        if (shouldRateLimit('markAllAsRead', 3000)) {
            console.warn('Rate limited: markAllAsRead called too frequently');
            return { success: true };
        }

        return retryWithBackoff(async () => {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.post(`${API_BASE_URL}/bookmyop/notifications/mark-all-read/`);
            return response.data;
        }, 2, 1000);
    },

    getUnreadNotificationCount: async () => {
        // Rate limit unread count calls
        if (shouldRateLimit('getUnreadCount', 3000)) {
            console.warn('Rate limited: getUnreadCount called too frequently');
            return { unread_count: 0 };
        }

        return retryWithBackoff(async () => {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            const response = await axios.get(`${API_BASE_URL}/bookmyop/notifications/count_unread/`);
            // Extract the data from the nested structure
            return response.data.data || response.data;
        }, 2, 1000);
    },

    // Search API - Unified search across doctors, service providers, and specialties
    search: async (searchParams = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            console.log('Search API call with params:', searchParams);
            const response = await axios.post(`${API_BASE_URL}/bookmyop/search/`, searchParams);
            console.log('Search API response:', response.data);
            return response.data;
        } catch (error) {
            console.error('Search API error:', error);
            throw error.response?.data || { message: 'Search failed' };
        }
    },

    // Location-Based Search APIs
    
    // Set session location (for guest users - NO AUTH REQUIRED)
    // Also used for authenticated users to sync profile location to session
    setSessionLocation: async (locationData) => {
        try {
            const url = `${API_BASE_URL}/bookmyop/session-location/`;
            console.log('🌐 BookMyOpAPI.setSessionLocation: Making API call to:', url);
            console.log('🌐 BookMyOpAPI.setSessionLocation: Payload:', locationData);
            
            // Don't send auth headers for session location (guest users)
            // But authenticated users can also use this endpoint
            const response = await axios.post(
                url, 
                locationData,
                {
                    withCredentials: true, // Send session cookie
                    headers: {
                        // Explicitly don't send Authorization header
                        'Authorization': undefined
                    }
                }
            );
            console.log('✅ BookMyOpAPI.setSessionLocation: Success:', response.data);
            return response.data;
        } catch (error) {
            console.error('❌ BookMyOpAPI.setSessionLocation: Error:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                url: error.config?.url
            });
            throw error.response?.data || { message: 'Failed to set session location' };
        }
    },

    // Update user location (for authenticated users - REQUIRES AUTH)
    updateLocation: async (locationData) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            // Ensure address is provided (required by backend)
            const payload = {
                ...locationData,
                address: locationData.address || 
                         (locationData.city && locationData.state 
                          ? `${locationData.city}, ${locationData.state}` 
                          : `${locationData.latitude}, ${locationData.longitude}`)
            };
            
            console.log('🌐 BookMyOpAPI.updateLocation: Making API call with payload:', payload);
            const response = await axios.post(`${API_BASE_URL}/bookmyop/location/update/`, payload);
            console.log('✅ BookMyOpAPI.updateLocation: Success:', response.data);
            return response.data;
        } catch (error) {
            console.error('❌ BookMyOpAPI.updateLocation: Error:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                url: error.config?.url
            });
            throw error.response?.data || { message: 'Failed to update location' };
        }
    },

    // Search nearby doctors
    searchNearbyDoctors: async (filters = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            const queryParams = new URLSearchParams();
            
            // Add filters to query params
            if (filters.radius) queryParams.append('radius', filters.radius);
            if (filters.specialtyId) queryParams.append('specialty_id', filters.specialtyId);
            
            if (filters.sortBy) queryParams.append('sort_by', filters.sortBy);
            if (filters.sortOrder) queryParams.append('sort_order', filters.sortOrder);
            
            const response = await axios.get(
                `${API_BASE_URL}/bookmyop/nearby/doctors/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to search nearby doctors' };
        }
    },

    // Search nearby providers (hospitals/clinics)
    searchNearbyProviders: async (filters = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            const queryParams = new URLSearchParams();
            
            // Add filters to query params
            if (filters.radius) queryParams.append('radius', filters.radius);
            if (filters.providerType) queryParams.append('provider_type', filters.providerType);
           
            if (filters.sortBy) queryParams.append('sort_by', filters.sortBy);
            if (filters.sortOrder) queryParams.append('sort_order', filters.sortOrder);
            
            const response = await axios.get(
                `${API_BASE_URL}/bookmyop/nearby/providers/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to search nearby providers' };
        }
    },

    // Get high-rated doctors (no auth required)
    getHighRatedDoctors: async (filters = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            const queryParams = new URLSearchParams();
            
            // Add filters to query params
            if (filters.limit) queryParams.append('limit', filters.limit);
          
            
            const response = await axios.get(
                `${API_BASE_URL}/bookmyop/high-rated/doctors/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch high-rated doctors' };
        }
    },

    // Get nearest doctors (requires auth)
    getNearestDoctors: async (filters = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            const queryParams = new URLSearchParams();
            
            // Add filters to query params
            if (filters.maxDistance) queryParams.append('max_distance', filters.maxDistance);
            if (filters.specialtyId) queryParams.append('specialty_id', filters.specialtyId);
            
            const response = await axios.get(
                `${API_BASE_URL}/bookmyop/nearest/doctors/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch nearest doctors' };
        }
    },

    // Get top-rated providers (no auth required)
    getTopRatedProviders: async (filters = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            const queryParams = new URLSearchParams();
            
            // Add filters to query params
            if (filters.providerType) queryParams.append('provider_type', filters.providerType);
            if (filters.city) queryParams.append('city', filters.city);
            if (filters.limit) queryParams.append('limit', filters.limit);
            
            
            const response = await axios.get(
                `${API_BASE_URL}/bookmyop/top-rated/providers/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch top-rated providers' };
        }
    },

    // Get nearest providers (requires auth)
    getNearestProviders: async (filters = {}) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            const queryParams = new URLSearchParams();
            
            // Add filters to query params
            if (filters.maxDistance) queryParams.append('max_distance', filters.maxDistance);
            if (filters.providerType) queryParams.append('provider_type', filters.providerType);
            
            const response = await axios.get(
                `${API_BASE_URL}/bookmyop/nearest/providers/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch nearest providers' };
        }
    },

    // Organization Type Management
    getOrganizationTypes: async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/bookmyop/org-types/`);
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to fetch organization types' };
        }
    },

    updateOrganizationTypePreference: async (orgTypeId) => {
        try {
            // Only get CSRF token if we don't have a valid one
            if (!csrfToken || !csrfTokenExpiry || Date.now() >= csrfTokenExpiry) {
                await BookMyOpAPI.getCSRFToken();
            }
            
            const formData = new FormData();
            formData.append('preferred_org_type', orgTypeId);

            const response = await axios.patch(
                `${API_BASE_URL}/bookmyop/profile/`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                }
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || { message: 'Failed to update organization type preference' };
        }
    }
};

// Initialize interceptors
BookMyOpAPI.setupAxiosInterceptors();

export default BookMyOpAPI;
